import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, chartLink, formatDateTime, formatGbp, formatNumber, formatQuantity } from '../api';
import { usePolling } from '../auth';
import { ActionBadge, Badge, Card, Empty, Money, Notice, Spinner } from '../components/ui';
import type { DashboardResponse, TradesResponse } from '../types';

const PAGE_SIZE = 50;

export default function Trades() {
  const [streamId, setStreamId] = useState<number | ''>('');
  const [side, setSide] = useState<'' | 'BUY' | 'SELL'>('');
  const [symbol, setSymbol] = useState('');
  const [offset, setOffset] = useState(0);

  const dashboard = usePolling<DashboardResponse>(() => api.dashboard(), null);
  const trades = usePolling<TradesResponse>(
    () =>
      api.trades({
        ...(streamId === '' ? {} : { streamId }),
        ...(side === '' ? {} : { side }),
        ...(symbol.trim() === '' ? {} : { symbol: symbol.trim().toUpperCase() }),
        limit: PAGE_SIZE,
        offset,
      }),
    30_000,
    [streamId, side, symbol, offset],
  );

  const rows = trades.data?.trades ?? [];
  const total = trades.data?.total ?? 0;

  function resetPaging(): void {
    setOffset(0);
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Trade log</h1>
          <p>
            Every simulated fill, with the price it was filled at, the costs applied, the data feed the
            price came from, and the reasoning the strategy gave at the time.
          </p>
        </div>
        <button className="btn" type="button" onClick={trades.reload}>
          Refresh
        </button>
      </div>

      <Card title="Filters">
        <div className="inline-form">
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="filter-stream">Stream</label>
            <select
              id="filter-stream"
              value={streamId}
              onChange={(event) => {
                setStreamId(event.target.value === '' ? '' : Number(event.target.value));
                resetPaging();
              }}
            >
              <option value="">All streams</option>
              {(dashboard.data?.streams ?? []).map((stream) => (
                <option key={stream.id} value={stream.id}>
                  {stream.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ maxWidth: 140 }}>
            <label htmlFor="filter-side">Side</label>
            <select
              id="filter-side"
              value={side}
              onChange={(event) => {
                setSide(event.target.value as '' | 'BUY' | 'SELL');
                resetPaging();
              }}
            >
              <option value="">Both</option>
              <option value="BUY">Buys</option>
              <option value="SELL">Sells</option>
            </select>
          </div>
          <div className="field" style={{ maxWidth: 160 }}>
            <label htmlFor="filter-symbol">Symbol</label>
            <input
              id="filter-symbol"
              type="text"
              placeholder="e.g. AAPL"
              value={symbol}
              onChange={(event) => {
                setSymbol(event.target.value);
                resetPaging();
              }}
            />
          </div>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setStreamId('');
              setSide('');
              setSymbol('');
              resetPaging();
            }}
          >
            Clear
          </button>
        </div>
      </Card>

      {trades.error && <Notice tone="error">{trades.error}</Notice>}

      <Card
        title="Fills"
        subtitle={`${total} trade${total === 1 ? '' : 's'} matching`}
        actions={
          total > PAGE_SIZE ? (
            <>
              <button
                className="btn btn-sm"
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <span className="mono-small">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                className="btn btn-sm"
                type="button"
                disabled={!trades.data?.hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </>
          ) : undefined
        }
      >
        {trades.loading && rows.length === 0 ? (
          <Spinner label="Loading trades" />
        ) : rows.length === 0 ? (
          <Empty>
            No trades yet. Run a cycle from the dashboard, or wait for the scheduler during market
            hours.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Stream</th>
                  <th>Side</th>
                  <th>Symbol</th>
                  <th className="num">Quantity</th>
                  <th className="num">Fill price</th>
                  <th className="num">Gross</th>
                  <th className="num">Costs</th>
                  <th className="num">Realised</th>
                  <th>Feed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((trade) => (
                  <tr key={trade.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(trade.createdAt)}
                      <div className="mono-small">{trade.tradingDay}</div>
                    </td>
                    <td>
                      {trade.streamName}
                      {trade.model && <div className="mono-small">{trade.model}</div>}
                    </td>
                    <td>
                      <ActionBadge action={trade.side} />
                    </td>
                    <td>
                      <Link className="sym" to={chartLink(trade.symbol, { trade: trade.id })} title="Open this trade on the chart">
                        {trade.symbol}
                      </Link>
                      <div className="mono-small">{trade.name}</div>
                    </td>
                    <td className="num">{formatQuantity(trade.quantity)}</td>
                    <td className="num">
                      {formatNumber(trade.price, 4)} {trade.currency}
                      {trade.currency !== 'GBP' && (
                        <div className="mono-small">fx {formatNumber(trade.fxRate, 4)}</div>
                      )}
                    </td>
                    <td className="num">{formatGbp(trade.grossGbp)}</td>
                    <td className="num">
                      {formatGbp(trade.feesGbp)}
                      <CostDetail breakdown={trade.feeBreakdown} />
                    </td>
                    <td className="num">
                      {trade.realisedPnlGbp === null ? (
                        <span className="flat">open</span>
                      ) : (
                        <Money value={trade.realisedPnlGbp} showSign />
                      )}
                    </td>
                    <td>
                      <Badge tone="grey">{trade.dataProvider ?? 'unknown'}</Badge>
                      {trade.confidence !== null && (
                        <div className="mono-small">conf {trade.confidence.toFixed(2)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {rows.length > 0 && (
        <Card title="Reasoning" subtitle="why each of these trades was placed">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rows.slice(0, 25).map((trade) => (
              <div
                key={trade.id}
                style={{ borderLeft: '2px solid var(--hair-strong)', paddingLeft: 12 }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ActionBadge action={trade.side} />
                  <Link className="sym" to={chartLink(trade.symbol, { trade: trade.id })}>
                    {trade.symbol}
                  </Link>
                  <span style={{ fontSize: 12, color: 'var(--ink-dim)' }}>
                    {trade.streamName} · {formatDateTime(trade.createdAt)}
                  </span>
                </div>
                <p className="reason" style={{ margin: '4px 0 0' }}>
                  {trade.reason ?? 'No reasoning recorded.'}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function CostDetail({ breakdown }: { breakdown: Record<string, number> }) {
  const parts: string[] = [];
  if (breakdown['stampDutyPence']) parts.push(`stamp £${(breakdown['stampDutyPence'] / 100).toFixed(2)}`);
  if (breakdown['fxFeePence']) parts.push(`fx £${(breakdown['fxFeePence'] / 100).toFixed(2)}`);
  if (breakdown['commissionPence']) parts.push(`comm £${(breakdown['commissionPence'] / 100).toFixed(2)}`);
  if (breakdown['ptmLevyPence']) parts.push(`ptm £${(breakdown['ptmLevyPence'] / 100).toFixed(2)}`);
  if (parts.length === 0) return null;
  return <div className="mono-small">{parts.join(' · ')}</div>;
}
